import React, { Fragment } from "react";

import img13 from "../../../images/service/webapp/app development.png";
import img14 from "../../../images/service/webapp/app development (2).png";
import img15 from "../../../images/service/webapp/app development (3).png";

// Service icons
import sIcon1 from "../../../images/icon/service-icon01.svg";
import sIcon2 from "../../../images/icon/service-icon02.svg";
import sIcon3 from "../../../images/icon/service-icon03.svg";
import sIcon4 from "../../../images/icon/service-icon04.svg";
import sIcon5 from "../../../images/icon/service-icon05.svg";
import sIcon6 from "../../../images/icon/service-icon06.svg";

// Common Components
import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Scrollbar from "../../../components/scrollbar/scrollbar";
import Footer from "../../../components/footer/Footer";

import HeroSection from "../../../components/PageSection/HeroSection";
import ServiceCardsSection from "../../../components/PageSection/ServiceCardSection";
import Benefit from "../../../components/PageSection/BenefitSection";
import KeyFeaturesSection from "../../../components/PageSection/KeyFeature";
import WhyChooseUsSection from "../../../components/PageSection/WhyChooseUsSection";
import FaqSection from "../../../components/PageSection/FAQsection";
import FinalCenteredCTA from "../../../components/PageSection/FinalCTA";
import Industries from "../../../components/Industries/Industries";
import TheorySection from "../../../components/PageSection/Theory";

// =====================
// DATA
// =====================

const serviceCards = [
  {
    icon: sIcon1,
    title: "Native Apps (iOS & Android)",
    content:
      "Swift and Kotlin native development delivering the best performance, native feel, and full platform feature access for premium user experiences.",
  },
  {
    icon: sIcon2,
    title: "Cross-Platform Apps",
    content:
      "Flutter and React Native solutions — write once, deploy everywhere. Fast delivery, shared codebase, native-like performance across iOS and Android.",
  },
  {
    icon: sIcon3,
    title: "Enterprise Mobile Solutions",
    content:
      "Complex enterprise apps with advanced security, backend integrations, offline capabilities, and scalable architecture for large organizations.",
  },
  {
    icon: sIcon4,
    title: "E-commerce & Marketplace Apps",
    content:
      "Feature-rich shopping apps and on-demand platforms with seamless checkout, real-time tracking, payment integration, and personalized experiences.",
  },
];

const benefits = [
  {
    title: "+75% User Retention",
    description:
      "Superior user retention rates through intuitive interfaces designed for engagement and seamless user experiences.",
    icon: sIcon1,
  },
  {
    title: "4.9★ App Store Rating",
    description:
      "Consistently high app store ratings achieved through quality development and user-centric design principles.",
    icon: sIcon2,
  },
  {
    title: "60% Faster Development",
    description:
      "Cross-platform expertise delivering 60% faster development compared to native-only approach while maintaining 95%+ code reusability.",
    icon: sIcon3,
  },
  {
    title: "Scalable Architecture",
    description:
      "Built to grow from MVP to millions of users with robust backend infrastructure and cloud-native design.",
    icon: sIcon4,
  },
];

const faqs = [
  {
    q: "Should I build a native or cross-platform app?",
    a: "It depends on your requirements. Native apps (Swift/Kotlin) offer maximum performance and platform-specific features, while cross-platform (Flutter/React Native) provides 60% faster development with 95%+ code reusability. We'll recommend the best approach based on your goals, budget, and timeline.",
  },
  {
    q: "How long does it take to develop a mobile app?",
    a: "Development timelines vary by complexity. A simple app takes 8-12 weeks, while complex apps with advanced features require 16-24 weeks. Cross-platform development can reduce time by 40-60% compared to building separate native apps.",
  },
  {
    q: "Do you handle App Store and Google Play submission?",
    a: "Yes, we provide full support for both App Store and Google Play submission, including app store optimization (ASO), compliance with guidelines, and ongoing maintenance to ensure your app stays approved and visible.",
  },
  {
    q: "Will my app work offline?",
    a: "We can implement offline functionality based on your requirements, enabling users to access core features without internet connectivity. Data syncs automatically when connection is restored, providing seamless user experience.",
  },
];

// =====================
// PAGE COMPONENT
// =====================

export default function AppDevelopment() {
  return (
    <Fragment>
      <Header />

      <PageTitle
        pageTitle="App Development"
        pagesub="High-performance mobile apps that users love and businesses scale with"
      />

      {/* HERO SECTION */}
      <HeroSection
        title={
          <>
            <span className="gradient-text">App Development</span>{" "}
            That Rules
          </>
        }
        subtitle={`We create powerful, engaging, high-performance mobile apps (iOS, Android, Cross-Platform) 
that users love, businesses scale with, and markets notice. From concept to launch and beyond, 
we're your partner in mobile success.`}
        buttonText="Build Your App"
        buttonLink="/contact"
        image={img13}
        imageAlt="Mobile App Development"
      />

      {/* ================= THEORY 1 ================= */}
      <TheorySection
        title="Why Mobile Apps Drive Business Growth"
        image={img14}
        paragraphs={[
          "With 90% of mobile time spent in apps and mobile commerce growing 25% annually, businesses need apps that stand out. Mobile apps are no longer optional — they're essential for reaching customers where they spend most of their time.",
          "Our development approach focuses on user engagement, retention, and monetization — delivering apps that users open daily and recommend to others. Using cutting-edge technologies like Flutter, React Native, Swift, and Kotlin, we build apps that perform flawlessly across platforms while maintaining the native feel users expect.",
        ]}
      />

      {/* SERVICES */}
      <ServiceCardsSection
        title="Our App Development Services"
        cards={serviceCards}
      />

      {/* ================= THEORY 2 ================= */}
      <TheorySection
        title="Our App Building Process"
        image={img15}
        paragraphs={[
          "Discovery & Design: We start with idea validation, user personas, and feature prioritization. Our design phase includes wireframes, interactive prototypes, and user testing to ensure intuitive interfaces that drive engagement.",
          "Development & Testing: Frontend and backend development with robust API architecture, third-party integrations, and performance optimization. Rigorous quality assurance and beta testing ensure flawless functionality.",
          "Launch & Growth: Complete App Store and Google Play submission support, ASO optimization, analytics setup, and post-launch monitoring to ensure your app succeeds and scales effectively.",
        ]}
      />

      {/* BENEFITS */}
      <Benefit
        title="Proven Results From Our Apps"
        benefits={benefits}
      />

      {/* KEY FEATURES */}
      <KeyFeaturesSection
        title="Key Features of Our Mobile Apps"
        subtitle="Native performance, intuitive design, and scalable architecture in every app"
        features={[
          {
            icon: sIcon1,
            title: "Cross-Platform Excellence",
            description:
              "60% faster development vs native-only approach, maintaining 95%+ code reusability while delivering native performance.",
          },
          {
            icon: sIcon2,
            title: "User-Centric Design",
            description:
              "Intuitive interfaces designed for engagement and retention, achieving 75%+ user retention rates and 4.9★ ratings.",
          },
          {
            icon: sIcon3,
            title: "Scalable Architecture",
            description:
              "Built to grow with your user base — from MVP to millions of users, with robust backend infrastructure.",
          },
          {
            icon: sIcon4,
            title: "Native Performance",
            description:
              "Lightning-fast performance with smooth animations and responsive interactions that feel truly native.",
          },
          {
            icon: sIcon5,
            title: "Offline Capabilities",
            description:
              "Smart offline functionality allowing users to access core features without internet connectivity.",
          },
          {
            icon: sIcon6,
            title: "App Store Success",
            description:
              "Full support for App Store and Google Play submission, optimization, ASO, and ongoing maintenance.",
          },
        ]}
      />

      <Industries />

      {/* WHY CHOOSE US */}
      <WhyChooseUsSection
        items={[
          "Native & cross-platform expertise (Swift, Kotlin, Flutter, React Native)",
          "User-centric design achieving 75%+ retention and 4.9★ ratings",
          "60% faster development with cross-platform mastery",
          "End-to-end support from concept to growth and scaling",
        ]}
      />

      {/* FAQ */}
      <FaqSection faqs={faqs} />

      {/* FINAL CTA */}
      <FinalCenteredCTA />

      <Footer />
      <Scrollbar />
    </Fragment>
  );
}
